import React from 'react';
import PropTypes from 'prop-types';

/**
 * Единый компонент карточки для всего приложения
 * Обеспечивает консистентный дизайн и поведение
 */
export const UnifiedCard = ({
  children,
  variant = 'default',
  size = 'md',
  type = 'default',
  interactive = false,
  disabled = false,
  className = '',
  onClick,
  ...props
}) => {
  // Базовые классы
  const baseClasses = [
    'unified-card',
    `unified-card--${variant}`,
    `unified-card--${size}`,
    `unified-card--${type}`,
  ];

  // Дополнительные классы
  if (interactive) baseClasses.push('unified-card--interactive');
  if (disabled) baseClasses.push('disabled');
  if (onClick) baseClasses.push('unified-card--clickable');

  const combinedClasses = [...baseClasses, className].filter(Boolean).join(' ');

  const handleClick = (e) => {
    if (disabled) return;
    if (onClick) onClick(e);
  };

  return (
    <div
      className={combinedClasses}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onClick && !disabled) {
          e.preventDefault();
          onClick(e);
        }
      }}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Контент карточки
 */
export const UnifiedCardContent = ({ 
  children, 
  className = '',
  ...props 
}) => {
  return (
    <div 
      className={`unified-card__content ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Заголовок карточки
 */
export const UnifiedCardHeader = ({ 
  children, 
  center = false, 
  column = false,
  className = '',
  ...props 
}) => {
  const headerClasses = [
    'unified-card__header',
    center && 'unified-card__header--center',
    column && 'unified-card__header--column',
    className
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={headerClasses}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Заголовок карточки
 */
export const UnifiedCardTitle = ({ 
  children, 
  level = 'h3',
  className = '',
  ...props 
}) => {
  const HeadingTag = level;
  const classes = `unified-card__title ${className}`;
  
  return (
    <HeadingTag 
      className={classes}
      {...props}
    >
      {children}
    </HeadingTag>
  );
};

/**
 * Подзаголовок карточки
 */
export const UnifiedCardSubtitle = ({ 
  children, 
  className = '',
  ...props 
}) => {
  return (
    <div 
      className={`unified-card__subtitle ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Описание карточки
 */
export const UnifiedCardDescription = ({ 
  children, 
  className = '',
  ...props 
}) => {
  return (
    <p 
      className={`unified-card__description ${className}`}
      {...props}
    >
      {children}
    </p>
  );
};

/**
 * Основной контент карточки
 */
export const UnifiedCardBody = ({ 
  children, 
  className = '',
  ...props 
}) => {
  return (
    <div 
      className={`unified-card__body ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Информационная строка
 */
export const UnifiedCardInfoRow = ({ 
  label, 
  value, 
  border = false,
  className = '',
  ...props 
}) => {
  const rowClasses = [
    'unified-card__info-row',
    border && 'unified-card__info-row--border',
    className
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={rowClasses}
      {...props}
    >
      <span className="unified-card__info-label">{label}</span>
      <span className="unified-card__info-value">{value}</span>
    </div>
  );
};

/**
 * Метаданные (баланс, статус и т.д.)
 */
export const UnifiedCardMeta = ({ 
  label, 
  value, 
  className = '',
  ...props 
}) => {
  return (
    <div 
      className={`unified-card__meta ${className}`}
      {...props}
    >
      {label && <div className="unified-card__meta-label">{label}</div>}
      <div className="unified-card__meta-value">{value}</div>
    </div>
  );
};

/**
 * Аватар или иконка
 */
export const UnifiedCardAvatar = ({ 
  children, 
  src, 
  alt = '',
  size = 'md',
  className = '',
  ...props 
}) => {
  const avatarClasses = [
    'unified-card__avatar',
    size && `unified-card__avatar--${size}`,
    className
  ].filter(Boolean).join(' ');

  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        className={avatarClasses}
        {...props}
      />
    );
  }

  return (
    <div 
      className={avatarClasses}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Кнопки действий
 */
export const UnifiedCardActions = ({ 
  children, 
  direction = 'horizontal',
  justify = 'flex-start',
  border = true,
  className = '',
  ...props 
}) => {
  const actionsClasses = [
    'unified-card__actions',
    `unified-card__actions--${direction}`,
    justify && `unified-card__actions--${justify}`,
    !border && 'unified-card__actions--no-border',
    className
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={actionsClasses}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Кнопка действия
 */
export const UnifiedCardActionButton = ({ 
  children, 
  variant = 'default',
  disabled = false,
  onClick,
  className = '',
  ...props 
}) => {
  const buttonClasses = [
    'unified-card__action-btn',
    variant && `unified-card__action-btn--${variant}`,
    disabled && 'disabled',
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      className={buttonClasses}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

/**
 * Футер карточки
 */
export const UnifiedCardFooter = ({ 
  children, 
  className = '',
  ...props 
}) => {
  return (
    <div 
      className={`unified-card__footer ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Медиа контент (изображение)
 */
export const UnifiedCardMedia = ({ 
  src, 
  alt = '', 
  height = 200,
  className = '',
  ...props 
}) => {
  return (
    <img
      src={src}
      alt={alt}
      className={`unified-card__media ${className}`}
      style={{ height: `${height}px` }}
      {...props}
    />
  );
};

// PropTypes для всех компонентов
UnifiedCard.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['default', 'elevated', 'outlined', 'filled', 'ghost']),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  type: PropTypes.oneOf(['default', 'info', 'profile', 'actions', 'list', 'media']),
  interactive: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
};

UnifiedCardContent.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

UnifiedCardHeader.propTypes = {
  children: PropTypes.node.isRequired,
  center: PropTypes.bool,
  column: PropTypes.bool,
  className: PropTypes.string,
};

UnifiedCardTitle.propTypes = {
  children: PropTypes.node.isRequired,
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  className: PropTypes.string,
};

UnifiedCardSubtitle.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

UnifiedCardDescription.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

UnifiedCardBody.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

UnifiedCardInfoRow.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  border: PropTypes.bool,
  className: PropTypes.string,
};

UnifiedCardMeta.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node.isRequired,
  className: PropTypes.string,
};

UnifiedCardAvatar.propTypes = {
  children: PropTypes.node,
  src: PropTypes.string,
  alt: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
};

UnifiedCardActions.propTypes = {
  children: PropTypes.node.isRequired,
  direction: PropTypes.oneOf(['horizontal', 'vertical']),
  justify: PropTypes.oneOf(['flex-start', 'center', 'spaced']),
  border: PropTypes.bool,
  className: PropTypes.string,
};

UnifiedCardActionButton.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

UnifiedCardFooter.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

UnifiedCardMedia.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  height: PropTypes.number,
  className: PropTypes.string,
};

export default UnifiedCard;